import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { materiais as api, type MaterialNaLista } from '../api/materiais'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { marcaDaRota } from './marcaDaRota'
import './usuarios.css'
import './materiais.css'

/**
 * Os materiais que o assistente envia (demanda 039).
 *
 * 🔴 **A pré-visualização não é enfeite:** quem cadastra precisa ver o que vai
 * sair para o cliente. No motor v0 o material era um caminho fixo no código, e a
 * única forma de conferir era mandar para alguém.
 *
 * E a **legenda aparece junto do arquivo** de propósito — é ela que chega na
 * conversa, e vê-la separada do material é vê-la fora do contexto em que será
 * lida.
 */
export function Materiais() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = marcaDaRota() ?? ''
  const podeEditar = pode('ParametroGerenciar')

  const [lista, setLista] = useState<MaterialNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [chave, setChave] = useState('')
  const [titulo, setTitulo] = useState('')
  const [legenda, setLegenda] = useState('')
  const entrada = useRef<HTMLInputElement>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.listar(marca))
    } catch {
      setFalhou(true)
    }
  }, [marca])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function cadastrar(evento: FormEvent) {
    evento.preventDefault()

    const arquivo = entrada.current?.files?.[0]
    if (!arquivo) return

    setSalvando(true)
    setErro(null)

    try {
      await api.criar(marca, { chave, titulo, legenda, arquivo })
      setChave('')
      setTitulo('')
      setLegenda('')
      if (entrada.current) entrada.current.value = ''
      await carregar()
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  async function alternar(material: MaterialNaLista) {
    await api.salvar(marca, material.id, {
      titulo: material.titulo,
      legenda: material.legenda,
      ativo: !material.ativo,
    })

    await carregar()
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('materiais.titulo')}</h1>
          <p className="usuarios__descricao">{t('materiais.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {podeEditar && (
        <Cartao titulo={t('materiais.novo')}>
          <form className="materiais__form" onSubmit={cadastrar}>
            <Campo
              rotulo={t('materiais.chave')}
              obrigatorio
              ajuda={t('materiais.chaveDica')}
              value={chave}
              onChange={(e) => setChave(e.target.value)}
            />

            <Campo
              rotulo={t('materiais.tituloDoMaterial')}
              obrigatorio
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />

            <label className="campo">
              <span className="campo__rotulo">
                {t('materiais.legenda')}
                <span className="campo__obrigatorio" aria-hidden="true">
                  *
                </span>
              </span>
              <textarea
                className="campo__entrada"
                rows={3}
                required
                value={legenda}
                onChange={(e) => setLegenda(e.target.value)}
              />
              <span className="materiais__nota">{t('materiais.legendaDica')}</span>
            </label>

            <label className="campo">
              <span className="campo__rotulo">{t('materiais.arquivo')}</span>
              <input ref={entrada} type="file" accept="image/*,application/pdf" required />
              <span className="materiais__nota">{t('materiais.arquivoDica')}</span>
            </label>

            <Botao type="submit" carregando={salvando}>
              {t('comum.salvar')}
            </Botao>
          </form>
        </Cartao>
      )}

      <Cartao titulo={t('materiais.cadastrados')}>
        {lista.length === 0 ? (
          <Estado
            tipo="vazio"
            titulo={t('materiais.vazio')}
            descricao={t('materiais.vazioDescricao')}
          />
        ) : (
          <ul className="materiais__lista">
            {lista.map((m) => (
              <li key={m.id} className={`materiais__item ${m.ativo ? '' : 'materiais__item--inativo'}`}>
                {/* A pré-visualização: é o que se vai enviar, do jeito que sai. */}
                {m.mime.startsWith('image/') ? (
                  <img
                    className="materiais__previa"
                    src={api.arquivo(marca, m.id)}
                    alt={t('materiais.previaDe', { titulo: m.titulo })}
                  />
                ) : (
                  <a className="materiais__previa materiais__previa--arquivo" href={api.arquivo(marca, m.id)}>
                    {m.nomeOriginal}
                  </a>
                )}

                <div className="materiais__dados">
                  <div className="materiais__linha">
                    <code className="materiais__chave">{m.chave}</code>
                    {!m.ativo && <span className="materiais__inativo">{t('materiais.inativo')}</span>}
                  </div>

                  <strong>{m.titulo}</strong>
                  <p className="materiais__legenda">{m.legenda}</p>

                  {podeEditar && (
                    <Botao variante="secundario" onClick={() => alternar(m)}>
                      {m.ativo ? t('materiais.desativar') : t('materiais.ativar')}
                    </Botao>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </section>
  )
}
